/**
 * OrdersTableView — Trioty Orders design (custom grid).
 *
 * Columns (design): Order (#code/date + status) · Total & Payment · Items ·
 * Customer · Location · Action. Attention rows get a status-colored inline-start
 * border; draft rows are quiet/dashed. Cells reuse the existing order-row
 * components so money/status/item logic stays in one place.
 */

import { useState } from "react";
import type { OrderStatus, SellerOrderListItem } from "@/lib/api/types";
import type { Locale } from "@/lib/i18n/config";
import { useTranslations, useLocale } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { CouponTicket } from "./order-row";
import { OrderHoverTooltip, type HoverTip } from "./order-row/OrderHoverTooltip";
import {
  actVisual,
  actColors,
  ActionGlyph,
  OrderStatusBadge,
} from "./order-row/order-visuals";
import { LocationCell } from "./order-row/LocationCell";
import { getStatusMeta } from "@/modules/orders/lib/order-status-meta";
import { getMoneyState } from "@/modules/orders/lib/order-money";
import { pickLocalized } from "@/lib/i18n/localized";
import { ImageThumb } from "@/shared/components/PlaceholderImage";
import { getOrderItemImageUrl } from "@/lib/order-image";
import { useThemeStore } from "@/shared/theme/useTheme";
import { relativeAgo } from "@/modules/orders/lib/order-status-display";

type Props = {
  orders: SellerOrderListItem[];
  onSelectOrder: (id: string) => void;
  onQuickAction: (order: SellerOrderListItem, targetStatus: OrderStatus) => void;
  onOpenQuickSheet: (order: SellerOrderListItem) => void;
  quickActionBusyId: string | null;
};

const GRID =
  "[grid-template-columns:minmax(0,1.15fr)_minmax(0,0.82fr)_minmax(0,1.4fr)_minmax(0,0.85fr)_minmax(0,0.95fr)_72px]";

function isDraftRow(order: SellerOrderListItem): boolean {
  return order.status === "draft";
}

export function OrdersTableView({
  orders,
  onSelectOrder,
  onQuickAction,
  onOpenQuickSheet,
  quickActionBusyId,
}: Props) {
  const t = useTranslations();
  const locale = useLocale();
  const isDark = useThemeStore((s) => s.resolvedDark);
  const [tip, setTip] = useState<HoverTip | null>(null);

  function makeQuickAction(order: SellerOrderListItem) {
    return (o: SellerOrderListItem, targetStatus: OrderStatus) => {
      const meta = getStatusMeta(order.status);
      if (
        meta.transition &&
        (meta.transition.needsEta || meta.transition.requiresDeclineReason)
      ) {
        onOpenQuickSheet(o);
      } else {
        onQuickAction(o, targetStatus);
      }
    };
  }

  return (
    <>
    <div className="overflow-x-auto rounded-[16px] border border-[color:var(--line2)] bg-[color:var(--card)]">
      <div className="min-w-[880px]">
        {/* ── Header ── */}
        <div
          className={`grid ${GRID} items-center border-b border-[color:var(--line)] bg-[color:var(--soft)] px-[18px] py-3 text-[11.5px] font-semibold text-[color:var(--text-muted)]`}
        >
          <span>{t(keys.orders.colOrder)}</span>
          <span>
            {t(keys.orders.colTotal)} &amp; {t(keys.orders.colPayment)}
          </span>
          <span>{t(keys.orders.colItems)}</span>
          <span>{t(keys.orders.colCustomer)}</span>
          <span>{t(keys.orders.colLocation)}</span>
          <span className="text-end">{t(keys.orders.colAction)}</span>
        </div>

        {/* ── Rows ── */}
        {orders.map((order) => {
          const draft = isDraftRow(order);
          const act = actVisual(order.status);
          const rowTone = !draft && act ? actColors(act.tone, isDark) : null;
          const ago = relativeAgo(order.createdAt, locale);
          return (
            <div
              key={order.id}
              role="button"
              tabIndex={0}
              onClick={() => onSelectOrder(order.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter") onSelectOrder(order.id);
              }}
              onMouseEnter={(e) => setTip({ order, x: e.clientX, y: e.clientY })}
              onMouseMove={(e) =>
                setTip((prev) => (prev ? { order, x: e.clientX, y: e.clientY } : prev))
              }
              onMouseLeave={() => setTip((prev) => (prev?.order.id === order.id ? null : prev))}
              style={
                rowTone
                  ? { boxShadow: `inset 3px 0 0 ${rowTone.accent}`, background: rowTone.row }
                  : undefined
              }
              className={[
                `grid ${GRID} cursor-pointer items-center border-b border-[color:var(--hair)] px-[18px] py-[13px] transition-colors last:border-b-0`,
                draft
                  ? "border-b-dashed border-b-[color:var(--dash)] bg-[color:var(--soft)] opacity-90"
                  : rowTone
                    ? ""
                    : "hover:bg-[color:var(--soft)]",
              ].join(" ")}
            >
              {/* Order: code/date + status */}
              <div className="flex min-w-0 flex-col items-start gap-1.5">
                <span className="inline-flex min-w-0 max-w-full items-baseline gap-[7px]">
                  <span dir="ltr" className="truncate font-mono text-[11.5px] font-semibold tracking-[0.02em] text-[color:var(--text-secondary)]">
                    #{order.orderCode}
                  </span>
                  {ago ? (
                    <span className="shrink-0 whitespace-nowrap text-[11px] text-[color:var(--text-muted)]">
                      {ago}
                    </span>
                  ) : null}
                </span>
                <OrderStatusBadge status={order.status} />
              </div>

              {/* Total & Payment */}
              <MoneyCol order={order} />

              {/* Items */}
              <div className="flex min-w-0 items-center gap-[11px]">
                <ItemThumb order={order} />
                <div className="min-w-0">
                  <div className="truncate text-[12px] font-semibold text-[color:var(--text-primary)]">
                    {itemTitle(order, locale)}
                  </div>
                  {order.itemCount > 1 ? (
                    <div className="mt-0.5 text-[11.5px] font-semibold text-[color:var(--text-muted)]">
                      +{order.itemCount - 1}
                    </div>
                  ) : order.couponCode ? (
                    <span onClick={(e) => e.stopPropagation()}>
                      <CouponTicket code={order.couponCode} />
                    </span>
                  ) : null}
                </div>
              </div>

              {/* Customer */}
              <div className="min-w-0">
                <div className="truncate text-[13px] font-semibold text-[color:var(--text-primary)]">
                  {order.customer.name || "—"}
                </div>
                {order.customer.phoneE164 ? (
                  <div className="mt-0.5 flex min-w-0 items-center gap-[5px]">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
                    </svg>
                    <span dir="ltr" className="truncate text-[11.5px] font-semibold text-[color:var(--text-muted)]">
                      {order.customer.phoneE164}
                    </span>
                  </div>
                ) : null}
              </div>

              {/* Location — LocationCell composes the structured address with a
                  locale-aware separator. This used to render the raw
                  `buyerAddress` string, which the backend joins with the Arabic
                  comma regardless of locale ("Street 9، 12، Maadi" in EN). */}
              <LocationCell
                address={order.address}
                buyerAddress={order.buyerAddress}
                governorateId={order.governorateId}
              />

              {/* Action */}
              <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
                {act && rowTone && getStatusMeta(order.status).transition ? (
                  <button
                    type="button"
                    title={t(getStatusMeta(order.status).nextLabelKey)}
                    aria-label={t(getStatusMeta(order.status).nextLabelKey)}
                    disabled={quickActionBusyId === order.id}
                    onClick={() => {
                      const target = getStatusMeta(order.status).transition?.targetStatus;
                      if (target) makeQuickAction(order)(order, target);
                    }}
                    className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[9px] transition-opacity hover:opacity-90 disabled:opacity-50"
                    style={{ background: rowTone.btnBg, color: rowTone.btnFg }}
                  >
                    {quickActionBusyId === order.id ? (
                      <svg aria-hidden="true" className="h-[14px] w-[14px] animate-spin" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="32" strokeDashoffset="12" />
                      </svg>
                    ) : (
                      <ActionGlyph kind={act.icon} />
                    )}
                  </button>
                ) : (
                  <button
                    type="button"
                    title={t(keys.orders.metaNextViewOrder)}
                    aria-label={t(keys.orders.metaNextViewOrder)}
                    onClick={() => onSelectOrder(order.id)}
                    className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[9px] border border-[color:var(--border-strong)] bg-[color:var(--card)] text-[color:var(--text-muted)] transition-colors hover:bg-[color:var(--soft)] hover:text-[color:var(--text-primary)]"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
    <OrderHoverTooltip tip={tip} />
    </>
  );
}

function itemTitle(order: SellerOrderListItem, locale: Locale): string {
  const first = order.items[0];
  if (!first) return "—";
  return (
    pickLocalized(
      locale,
      first.resolvedTitleAr || first.productNameAr,
      first.resolvedTitleEn || first.productNameEn,
    ) ||
    first.resolvedTitleAr ||
    first.productNameAr
  );
}

/** Total + money-clarity line, sized/colored to the Trioty design. */
function MoneyCol({ order }: { order: SellerOrderListItem }) {
  const t = useTranslations();
  const money = getMoneyState(
    order.status,
    order.paymentMethod,
    order.paidAmount,
    order.requiredAmount,
    order.totalAmount,
  );
  const lineText =
    Object.keys(money.lineParams).length > 0
      ? t(money.lineKey, money.lineParams as Record<string, string>)
      : t(money.lineKey);
  return (
    <div className="min-w-0">
      <div className="text-[14px] font-bold text-[color:var(--text-primary)]">
        {money.headlineFormatted}
      </div>
      <div className="mt-[3px] truncate text-[11.5px] text-[color:var(--text-muted)]">
        {lineText}
      </div>
    </div>
  );
}

/** 34×34 product image (real photo, placeholder fallback) + ×qty badge. */
function ItemThumb({ order }: { order: SellerOrderListItem }) {
  const locale = useLocale();
  const first = order.items[0] ?? null;
  const qty = first?.quantity ?? 0;
  return (
    <span className="relative shrink-0">
      <ImageThumb
        src={first ? getOrderItemImageUrl(first) : null}
        seed={first?.productId}
        alt={itemTitle(order, locale)}
        size="h-[34px] w-[34px]"
        bg="bg-white"
        className="rounded-[9px]"
      />
      {qty > 1 ? (
        <span className="absolute -bottom-[6px] -end-[6px] flex h-[18px] min-w-[18px] items-center justify-center rounded-full border-2 border-[color:var(--card)] bg-[color:var(--surface-alt)] px-1 text-[10px] font-bold text-[color:var(--text-secondary)]">
          ×{qty}
        </span>
      ) : null}
    </span>
  );
}

